import React, { useState, useEffect } from 'react';
import { 
  Trophy, Award, Star, Flame, Target, TrendingUp, 
  BookOpen, CheckCircle, Clock, Zap, Crown, Medal
} from 'lucide-react';
import { courseService } from '../../../services/courseService';

export const XPAchievements = ({ bookId, user, userProfile }) => {
  const [stats, setStats] = useState({
    totalXP: 0,
    level: 1,
    currentLevelXP: 0,
    nextLevelXP: 100,
    streak: 0,
    totalCourses: 0,
    completedCourses: 0,
    totalLessons: 0,
    minutesLearned: 0
  });
  const [achievements, setAchievements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAchievementData();
  }, [user, bookId]);

  const loadAchievementData = async () => {
    try {
      // Load user stats
      const profile = userProfile || await courseService.getUserProfile(user.uid, bookId);
      const progress = await courseService.getUserAllProgress(user.uid, bookId);

      const totalXP = profile.xp || 0;
      const level = Math.floor(totalXP / 100) + 1;
      const currentLevelXP = totalXP % 100;
      const nextLevelXP = 100;

      const completedCourses = progress.filter(p => p.completionPercentage === 100).length;
      const totalLessons = progress.reduce((sum, p) => sum + (p.completedLessons?.length || 0), 0);

      setStats({
        totalXP,
        level,
        currentLevelXP,
        nextLevelXP,
        streak: profile.streak || 0,
        totalCourses: progress.length,
        completedCourses,
        totalLessons,
        minutesLearned: profile.minutesLearned || 0
      });

      // Load achievements
      const allAchievements = [
        {
          id: 'first-course',
          title: 'Getting Started',
          description: 'Complete your first course',
          icon: BookOpen,
          color: 'blue',
          xp: 50,
          unlocked: completedCourses >= 1,
          progress: Math.min(completedCourses, 1),
          total: 1
        },
        {
          id: '5-courses',
          title: 'Dedicated Learner',
          description: 'Complete 5 courses',
          icon: Award,
          color: 'purple',
          xp: 200,
          unlocked: completedCourses >= 5,
          progress: Math.min(completedCourses, 5),
          total: 5
        },
        {
          id: '7-day-streak',
          title: 'Week Warrior',
          description: 'Maintain a 7-day learning streak',
          icon: Flame,
          color: 'orange',
          xp: 100,
          unlocked: (profile.streak || 0) >= 7,
          progress: Math.min(profile.streak || 0, 7),
          total: 7
        },
        {
          id: '30-day-streak',
          title: 'Consistency Champion',
          description: 'Maintain a 30-day learning streak',
          icon: Flame,
          color: 'red',
          xp: 500,
          unlocked: (profile.streak || 0) >= 30,
          progress: Math.min(profile.streak || 0, 30),
          total: 30
        },
        {
          id: '50-lessons',
          title: 'Knowledge Seeker',
          description: 'Complete 50 lessons',
          icon: CheckCircle,
          color: 'green',
          xp: 250,
          unlocked: totalLessons >= 50,
          progress: Math.min(totalLessons, 50),
          total: 50
        },
        {
          id: '1000-minutes',
          title: 'Time Investment',
          description: 'Learn for 1000 minutes',
          icon: Clock,
          color: 'yellow',
          xp: 300,
          unlocked: (profile.minutesLearned || 0) >= 1000,
          progress: Math.min(profile.minutesLearned || 0, 1000),
          total: 1000
        },
        {
          id: 'perfect-score',
          title: 'Perfect Score',
          description: 'Get 100% on any assessment',
          icon: Star,
          color: 'yellow',
          xp: 150,
          unlocked: profile.perfectScores > 0,
          progress: profile.perfectScores > 0 ? 1 : 0,
          total: 1
        },
        {
          id: 'speed-learner',
          title: 'Speed Learner',
          description: 'Complete a course in one day',
          icon: Zap,
          color: 'blue',
          xp: 200,
          unlocked: profile.speedLearner || false,
          progress: profile.speedLearner ? 1 : 0,
          total: 1
        }
      ];

      setAchievements(allAchievements);

    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = achievements.filter(a => !a.unlocked);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">XP & Achievements</h1>
        <p className="text-gray-600">Track your learning progress and unlock achievements</p>
      </div>

      {/* Level & XP Card */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-sm font-medium mb-2 opacity-90">Current Level</div>
            <div className="text-5xl font-bold">{stats.level}</div>
          </div>
          <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <Crown className="w-12 h-12" />
          </div>
        </div>

        <div className="mb-2">
          <div className="flex items-center justify-between text-sm mb-2">
            <span>Level Progress</span>
            <span>{stats.currentLevelXP} / {stats.nextLevelXP} XP</span>
          </div>
          <div className="w-full bg-white bg-opacity-30 rounded-full h-3">
            <div 
              className="bg-white h-3 rounded-full transition-all"
              style={{ width: `${(stats.currentLevelXP / stats.nextLevelXP) * 100}%` }}
            />
          </div>
        </div>

        <div className="text-sm opacity-90">
          {stats.nextLevelXP - stats.currentLevelXP} XP to next level
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="w-8 h-8 text-yellow-500" />
            <span className="text-2xl font-bold text-gray-900">{stats.totalXP}</span>
          </div>
          <div className="text-sm text-gray-600">Total XP</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Flame className="w-8 h-8 text-orange-500" />
            <span className="text-2xl font-bold text-gray-900">{stats.streak}</span>
          </div>
          <div className="text-sm text-gray-600">Day Streak</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <span className="text-2xl font-bold text-gray-900">{stats.completedCourses}</span>
          </div>
          <div className="text-sm text-gray-600">Courses Completed</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-blue-500" />
            <span className="text-2xl font-bold text-gray-900">
              {Math.floor(stats.minutesLearned / 60)}h
            </span>
          </div>
          <div className="text-sm text-gray-600">Time Learned</div>
        </div>
      </div>

      {/* Achievements */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Achievements</h2>
          <div className="text-sm text-gray-600">
            {unlockedAchievements.length} of {achievements.length} unlocked
          </div>
        </div>

        {/* Unlocked Achievements */}
        {unlockedAchievements.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Unlocked</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {unlockedAchievements.map((achievement) => {
                const Icon = achievement.icon;
                return (
                  <div 
                    key={achievement.id}
                    className="bg-white rounded-xl p-6 border-2 border-green-200"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 bg-${achievement.color}-100 rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 text-${achievement.color}-600`} />
                      </div>
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">{achievement.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-semibold text-yellow-600">
                        +{achievement.xp} XP
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Locked Achievements */}
        {lockedAchievements.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Locked</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lockedAchievements.map((achievement) => {
                const Icon = achievement.icon;
                const progressPercent = (achievement.progress / achievement.total) * 100;
                
                return (
                  <div 
                    key={achievement.id}
                    className="bg-white rounded-xl p-6 border border-gray-200 opacity-75"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-gray-400" />
                      </div>
                      <Medal className="w-6 h-6 text-gray-300" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">{achievement.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                    
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{achievement.progress} / {achievement.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-500">
                        +{achievement.xp} XP
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};